import React, { useState, useEffect } from 'react';
import { useController } from 'react-hook-form';
import InfiniteSelect from '../InfiniteSelect/InfiniteSelect';
import PricesDisplay from '../PricesDisplay';
import { Price as CommonPrice } from '../../types/common';
import {
  Container,
  FlexContainer,
  MainSelectContainer,
  UnitSelectContainer,
  SelectContainer,
  UnitSelectionContainer,
  UnitLabel,
  UnitSelect,
  PricesContainer,
  NoUnitMessage
} from './style';

interface Option {
  id: any;
  name: string;
  code?: string;
  disabled?: boolean;
  category?: string;
  type?: string;
  prices?: Array<{
    id?: number | string;
    price: string;
    unitOfMeasurement?: {
      id: number;
      name: string;
    };
  }>;
}

interface ResourceServiceSelectProps {
  name: string;
  control: any;
  placeholder?: string;
  searchPlaceholder?: string;
  fetchFunction: (limit: number, offset: number, search?: string) => Promise<Option[]>;
  hasError?: boolean;
  disabled?: boolean;
  unitFieldName?: string;
  onUnitChange?: (unitId: number) => void;
}

const ResourceServiceSelect: React.FC<ResourceServiceSelectProps> = ({
  name,
  control,
  placeholder,
  searchPlaceholder,
  fetchFunction,
  hasError,
  disabled,
  unitFieldName,
  onUnitChange
}) => {
  const { field: mainField } = useController({
    name,
    control,
  });

  const { field: unitField } = useController({
    name: unitFieldName || `${name}.unitOfMeasurementId`,
    control,
  });

  const [selectedOption, setSelectedOption] = useState<Option | null>(mainField.value);

  useEffect(() => {
    setSelectedOption(mainField.value);
  }, [mainField.value]);

  const handleOptionSelect = (option: Option | null) => {
    setSelectedOption(option);
    mainField.onChange(option);

    if (unitField) {
      unitField.onChange(null);
    }
  };

  const handleUnitSelect = (unitId: number) => {
    if (unitField) {
      unitField.onChange(unitId);
    }

    if (onUnitChange) {
      onUnitChange(unitId);
    }
  };

  const getOptionLabel = (option: Option) => {
    return option.code ? `${option.code} - ${option.name}` : option.name;
  };

  const renderOptionDetails = (option: Option) => {
    const details = [];

    if (option.id) {
      details.push(`ID: ${option.id}`);
    }
    if (option.category) {
      details.push(`Categoria: ${option.category}`);
    }
    if (option.type) {
      details.push(`Tipo: ${option.type}`);
    }

    if (option.prices && option.prices.length > 0) {
      const priceInfo = option.prices.map(p =>
        `R$ ${parseFloat(p.price).toFixed(2)}/${p.unitOfMeasurement?.name || 'unidade'}`
      ).join(', ');
      details.push(`Preços: ${priceInfo}`);
    }

    return details.join(' | ');
  };

  const enhancedFetchFunction = async (limit: number, offset: number, search?: string) => {
    const options = await fetchFunction(limit, offset, search);
    return options as any;
  };

  const availableUnits = selectedOption?.prices?.map(price => ({
    id: price.unitOfMeasurement?.id || 0,
    name: price.unitOfMeasurement?.name || 'Unidade',
    price: price.price
  })) || [];

  const shouldShowUnitSelection = availableUnits.length > 1;

  return (
    <Container>
      <InfiniteSelect
        name={name}
        control={control}
        placeholder={placeholder}
        searchPlaceholder={searchPlaceholder}
        fetchFunction={enhancedFetchFunction}
        hasError={hasError}
        disabled={disabled}
        onSelect={handleOptionSelect}
        getOptionLabel={getOptionLabel}
      />
    </Container>
  );
};

export default ResourceServiceSelect;
